
import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, Menu, X } from 'lucide-react';
import './Header.css';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [openMobileAccordion, setOpenMobileAccordion] = useState(null);

  const location = useLocation();
  const closeTimerRef = useRef(null);

  const openMenu = (name) => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    setOpenDropdown(name);
  };

  const scheduleClose = () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);

    closeTimerRef.current = setTimeout(() => {
      setOpenDropdown(null);
      closeTimerRef.current = null;
    }, 180);
  };

  useEffect(() => {
    return () => {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    handleScroll();

    window.addEventListener('scroll', handleScroll);

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setOpenMobileAccordion(null);
    setOpenDropdown(null);
  }, [location]);

  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen
      ? 'hidden'
      : '';

    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  const productsMenu = [
    { name: '看板・サイン', path: '/products/signage' },
    { name: 'LED照明', path: '/products/led-lighting' },
    { name: 'LEDビジョン', path: '/products/led-vision' },
    { name: '電光掲示板', path: '/products/electric-board' },
    { name: '防犯カメラ', path: '/products/security-camera' },
    { name: 'パーキングシステム', path: '/products/parking-system' },
    { name: '緊急出動警報板', path: '/products/emergency-board' },
    { name: 'カスタム金物製作', path: '/products/custom-metal' },
  ];

  const constructionMenu = [
    { name: 'ご相談の流れ', path: '/construction-support#consultation' },
    { name: '現場調査', path: '/construction-support#survey' },
    { name: 'ご提案・お見積り', path: '/construction-support#proposal' },
    { name: '施工対応', path: '/construction-support#construction' },
    { name: '施工後サポート', path: '/construction-support#support' },
    { name: 'よくある質問', path: '/construction-support#faq' },
  ];

  const companyMenu = [
    { name: '会社概要', path: '/company#company-overview' },
    { name: '事業内容', path: '/company#business' },
    { name: '許可・登録', path: '/company#licenses' },
    { name: '保有資格・技能講習', path: '/company#qualifications' },
    { name: 'アクセス', path: '/company#access' },
  ];

  const toggleMobileAccordion = (menu) => {
    setOpenMobileAccordion((current) =>
      current === menu ? null : menu
    );
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
    setOpenMobileAccordion(null);
  };

  return (
    <header className={`header ${isScrolled ? 'scrolled' : ''}`}>
      <div className="header-container">
        <Link
          to="/"
          className="logo"
          data-testid="site-logo"
          onClick={closeMobileMenu}
        >
          <img
            src="/images/hs-logo.png"
            alt="株式会社エイチエス"
            className="logo-img"
          />
        </Link>

        {/* ===== Desktop Navigation ===== */}
        <nav className="desktop-nav" aria-label="メインナビゲーション">
          <Link to="/" className="nav-link">
            ホーム
          </Link>

          {/* Products */}
          <div
            className="nav-item-dropdown"
            onMouseEnter={() => openMenu('products')}
            onMouseLeave={scheduleClose}
          >
            <button
              type="button"
              className="nav-link"
              aria-expanded={openDropdown === 'products'}
              onClick={() =>
                openDropdown === 'products'
                  ? setOpenDropdown(null)
                  : openMenu('products')
              }
            >
              製品・サービス
              <ChevronDown size={16} />
            </button>

            {openDropdown === 'products' && (
              <div className="dropdown-menu">
                {productsMenu.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className="dropdown-item"
                    onClick={() => setOpenDropdown(null)}
                  >
                    {item.name}
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* NEW: Works */}
          <Link
            to="/works"
            className="nav-link"
            aria-current={
              location.pathname === '/works' ? 'page' : undefined
            }
          >
            施工実績
          </Link>

          {/* Construction Support */}
          <div
            className="nav-item-dropdown"
            onMouseEnter={() => openMenu('construction')}
            onMouseLeave={scheduleClose}
          >
            <button
              type="button"
              className="nav-link"
              aria-expanded={openDropdown === 'construction'}
              onClick={() =>
                openDropdown === 'construction'
                  ? setOpenDropdown(null)
                  : openMenu('construction')
              }
            >
              施工・サポート
              <ChevronDown size={16} />
            </button>

            {openDropdown === 'construction' && (
              <div className="dropdown-menu">
                {constructionMenu.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className="dropdown-item"
                    onClick={() => setOpenDropdown(null)}
                  >
                    {item.name}
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Company */}
          <div
            className="nav-item-dropdown"
            onMouseEnter={() => openMenu('company')}
            onMouseLeave={scheduleClose}
          >
            <button
              type="button"
              className="nav-link"
              aria-expanded={openDropdown === 'company'}
              onClick={() =>
                openDropdown === 'company'
                  ? setOpenDropdown(null)
                  : openMenu('company')
              }
            >
              会社概要
              <ChevronDown size={16} />
            </button>

            {openDropdown === 'company' && (
              <div className="dropdown-menu">
                {companyMenu.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className="dropdown-item"
                    onClick={() => setOpenDropdown(null)}
                  >
                    {item.name}
                  </Link>
                ))}
              </div>
            )}
          </div>

          <Link
            to="/contact"
            className="nav-link contact-link"
          >
            お問い合わせ
          </Link>
        </nav>

        {/* ===== Mobile Menu Button ===== */}
        <button
          type="button"
          className="mobile-menu-button"
          onClick={() =>
            setIsMobileMenuOpen((current) => !current)
          }
          aria-label={
            isMobileMenuOpen ? 'メニューを閉じる' : 'メニューを開く'
          }
          aria-expanded={isMobileMenuOpen}
        >
          {isMobileMenuOpen ? (
            <X size={24} />
          ) : (
            <Menu size={24} />
          )}
        </button>
      </div>

      {/* ===== Mobile Navigation ===== */}
      {isMobileMenuOpen && (
        <div className="mobile-menu">
          <Link
            to="/"
            className="mobile-nav-link"
            onClick={closeMobileMenu}
          >
            ホーム
          </Link>

          {/* Products Accordion */}
          <div className="mobile-accordion">
            <button
              type="button"
              className="mobile-accordion-button"
              onClick={() => toggleMobileAccordion('products')}
              aria-expanded={openMobileAccordion === 'products'}
            >
              製品・サービス
              <ChevronDown
                size={18}
                className={
                  openMobileAccordion === 'products'
                    ? 'rotated'
                    : ''
                }
              />
            </button>

            {openMobileAccordion === 'products' && (
              <div className="mobile-accordion-content">
                {productsMenu.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className="mobile-sub-link"
                    onClick={closeMobileMenu}
                  >
                    {item.name}
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* NEW: Works */}
          <Link
            to="/works"
            className="mobile-nav-link"
            onClick={closeMobileMenu}
            aria-current={
              location.pathname === '/works' ? 'page' : undefined
            }
          >
            施工実績
          </Link>

          {/* Construction Accordion */}
          <div className="mobile-accordion">
            <button
              type="button"
              className="mobile-accordion-button"
              onClick={() =>
                toggleMobileAccordion('construction')
              }
              aria-expanded={
                openMobileAccordion === 'construction'
              }
            >
              施工・サポート
              <ChevronDown
                size={18}
                className={
                  openMobileAccordion === 'construction'
                    ? 'rotated'
                    : ''
                }
              />
            </button>

            {openMobileAccordion === 'construction' && (
              <div className="mobile-accordion-content">
                {constructionMenu.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className="mobile-sub-link"
                    onClick={closeMobileMenu}
                  >
                    {item.name}
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Company Accordion */}
          <div className="mobile-accordion">
            <button
              type="button"
              className="mobile-accordion-button"
              onClick={() =>
                toggleMobileAccordion('company')
              }
              aria-expanded={
                openMobileAccordion === 'company'
              }
            >
              会社概要
              <ChevronDown
                size={18}
                className={
                  openMobileAccordion === 'company'
                    ? 'rotated'
                    : ''
                }
              />
            </button>

            {openMobileAccordion === 'company' && (
              <div className="mobile-accordion-content">
                {companyMenu.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className="mobile-sub-link"
                    onClick={closeMobileMenu}
                  >
                    {item.name}
                  </Link>
                ))}
              </div>
            )}
          </div>

          <Link
            to="/contact"
            className="mobile-nav-link contact"
            onClick={closeMobileMenu}
          >
            お問い合わせ
          </Link>
        </div>
      )}
    </header>
  );
};

export default Header;
